import { FC, useMemo, useCallback, CSSProperties } from "react";
import { Box, Link, system, SystemProps, Text, LegacyText } from "flicket-ui";
import get from "lodash/get";
import { createEditor } from "slate";
import { Slate, Editable, withReact, RenderElementProps } from "slate-react";

import styled, { DefaultTheme } from "styled-components";
import { parse } from "./parse";
import { VideoPlayer } from "../VideoPlayer";

const fontStylesRegular = (theme: DefaultTheme) => `
  font-size: ${theme.fontSizes[3]};
  line-height: 1.5;
  font-weight: ${theme.fontWeights.regular};
  color: ${theme.colors.N800};
`;

export interface SlateRendererProps extends SystemProps {
  placeholder?: string;
  value: string;
  type?: "merch" | "experienceSales";
}

export const SlateContainer = styled(Box)<{
  type?: SlateRendererProps["type"];
}>`
  p,
  a,
  li {
    ${(p) => fontStylesRegular(p.theme)}
  }

  h1,
  h2,
  h3 {
    margin-bottom: ${(p) => p.theme.space[1]}px;
  }

  p,
  ul,
  ol {
    margin-bottom: ${(p) => p.theme.space["1/2"]}px;
  }

  ul li,
  ol li {
    position: relative;
    margin-left: ${(p) => p.theme.space[4]}px;
  }

  ol li::marker {
    font-size: ${(p) => p.theme.fontSizes[2]};
  }

  a {
    text-decoration: underline;

    &:hover {
      opacity: 0.7;
    }
  }

  ${system}
`;

export const SlateRenderer: FC<SlateRendererProps> = ({
  placeholder,
  value: stringifiedValue,
  type,
  ...props
}) => {
  const value: any = parse(stringifiedValue);
  const editor = useMemo(() => withReact(createEditor()), []);

  const renderer = experienceSalesRenderer;

  const renderElement = useCallback(renderer, []);

  const renderLeaf = useCallback(({ attributes, children, leaf }) => {
    if (leaf.bold) {
      children = <strong>{children}</strong>;
    }

    if (leaf.italic) {
      children = <em>{children}</em>;
    }

    if (leaf.underline) {
      children = <u>{children}</u>;
    }

    return <span {...attributes}>{children}</span>;
  }, []);

  return (
    <SlateContainer type={type} {...props}>
      {/*eslint-disable-next-line @typescript-eslint/no-empty-function*/}
      <Slate editor={editor} onChange={() => {}} readOnly value={value}>
        <Editable
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          readOnly
          placeholder={placeholder}
        />
      </Slate>
    </SlateContainer>
  );
};

export default SlateRenderer;

function defaultRenderer_legacy({
  attributes,
  children,
  element,
}: RenderElementProps) {
  switch (element.type) {
    case "bulleted-list":
      return <ul {...attributes}>{children}</ul>;
    case "heading-one":
      return <h1 {...attributes}>{children}</h1>;
    case "heading-two":
      return <h2 {...attributes}>{children}</h2>;
    case "list-item":
      return <li {...attributes}>{children}</li>;
    case "numbered-list":
      return <ol {...attributes}>{children}</ol>;
    case "link": {
      let url = get(element, "url", get(element, "data.url")) as string;
      if (url?.indexOf("://") === -1) {
        url = `https://${url}`;
      }
      return (
        <a href={url} target="_blank" rel="noopener noreferrer">
          {element.content || children}
        </a>
      );
    }
    default:
      return <p {...attributes}>{children}</p>;
  }
}

function experienceSalesRenderer({
  attributes,
  children,
  element,
}: RenderElementProps) {
  switch (element.type) {
    case "heading-one":
      return (
        <Text as="h1" variant="header.L" {...attributes}>
          {children}
        </Text>
      );
    case "heading-two":
      return (
        <Text as="h2" variant="header.M" {...attributes}>
          {children}
        </Text>
      );
    case "heading-three":
      return (
        <Text as="h3" variant="header.S" {...attributes}>
          {children}
        </Text>
      );
    case "bulleted-list":
      return <ul {...attributes}>{children}</ul>;
    case "numbered-list":
      return <ol {...attributes}>{children}</ol>;
    case "list-item":
      return <li {...attributes}>{children}</li>;

    case "link": {
      let url = get(element, "url", get(element, "data.url")) as string;
      if (url?.indexOf("://") === -1) {
        url = `https://${url}`;
      }
      return (
        <a href={url} target="_blank" rel="noopener noreferrer">
          {element.content || children}
        </a>
      );
    }
    case "image":
      return (
        <RenderImage attributes={attributes} element={element}>
          {children}
        </RenderImage>
      );
      break;
    case "video":
      return (
        <RenderVideo attributes={attributes} element={element}>
          {children}
        </RenderVideo>
      );
      break;
    default:
      return (
        <Text as="p" variant="regular" {...attributes}>
          {children}
        </Text>
      );
  }
}

function RenderImage({ attributes, element, children }: RenderElementProps) {
  let imageStyle: CSSProperties = {};

  if (element.size) {
    const width = (() => {
      if (element.size === "small") return "33%";
      if (element.size === "medium") return "50%";
      if (element.size === "large") return "100%";
      return "100%";
    })();

    imageStyle = {
      width,
      borderRadius: "4px",
    };
  }

  if (element.align) {
    imageStyle = {
      ...imageStyle,
      margin: `auto ${element.align === "right" ? "0" : "auto"} auto ${
        element.align === "left" ? "0" : "auto"
      }`,
      textAlign: element.align as "left" | "center" | "right",
    };
  }

  const ImageComponent = (
    <img
      {...attributes}
      style={imageStyle}
      src={element.url as string}
      alt="/static/no-image.jpg"
    />
  );

  return element.href ? (
    <Link href={(element.href as string) ?? "/link"} target="_new">
      {ImageComponent}
    </Link>
  ) : (
    ImageComponent
  );
}

function RenderVideo({ attributes, element, children }: RenderElementProps) {
  const videoWrapper: CSSProperties = {
    position: "relative",
    paddingBottom: "56.25%" /* Player ratio: 100 / (1280 / 720) */,
    width: "100%",
    height: 0,
    borderRadius: 4,
    overflow: "hidden",
  };

  const iframeStyle: CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
  };

  return (
    <Box {...attributes} style={videoWrapper}>
      <VideoPlayer
        url={element.url}
        style={iframeStyle}
        width="100%"
        height="100%"
        controls={true}
      />
    </Box>
  );
}
